// components/ds: the display primitives (Price, Skeleton, Stepper, the
// empty, error and success states, the sticky action bar).
import { plural } from "@/lib/format";

const counted = (lang, forms) => (n) => plural(n, forms, { lang });

// French elides "de" before a vowel: "heure de Lisbonne", "heure d’Accra".
const frOf = (place) => (/^[aeiouyàâäéèêëîïôöûüœ]/i.test(place) ? `d’${place}` : `de ${place}`);

export const dsL = {
  en: {
    loading: "Loading…",
    // components/ui/password-input: the eye that shows what is typed.
    passwordVisibility: { show: "Show password", hide: "Hide password" },
    price: {
      per: { night: "night", day: "day", person: "person", roomNight: "room / night" },
      unit: (amount, per) => `${amount} / ${per}`,
      charged: (amount) => `Charged: ${amount}`,
      original: "Original price",
    },
    count: {
      nights: counted("en", { one: "# night", other: "# nights" }),
      days: counted("en", { one: "# day", other: "# days" }),
      travellers: counted("en", { one: "# traveller", other: "# travellers" }),
      rooms: counted("en", { one: "# room", other: "# rooms" }),
      adults: counted("en", { one: "# adult", other: "# adults" }),
      children: counted("en", { one: "# child", other: "# children" }),
      results: counted("en", { one: "# result", other: "# results" }),
    },
    localTime: (place) => (place ? `${place} time` : "local time"),
    stepper: { label: "Booking steps", done: "completed", stepOf: (i, n) => `Step ${i} of ${n}` },
    error: {
      network: { title: "This page didn’t load", body: "Check your connection, then try again.", action: "Try again" },
      generic: { title: "Something went wrong on our side", body: "Try again in a moment. If it keeps happening, contact support.", action: "Try again" },
      notFound: { title: "We can’t find this page", body: "The link may be out of date. Start again from the home page.", action: "Go to the home page" },
      expired: { title: "This quote has expired", body: "Prices and availability change. Search again with the same details.", action: "Search again" },
    },
    success: {
      reference: "Booking reference",
      processing: { badge: "Payment pending", title: "Confirming your payment", body: "Keep this page open and don’t start another payment." },
      awaiting_partner: { badge: "Awaiting confirmation", title: "Request sent, waiting for confirmation", body: "Your booking is confirmed only once the provider accepts it." },
      confirmed: { badge: "Confirmed", title: "Your booking is confirmed", body: "" },
      failed: { badge: "Not confirmed", title: "Payment not confirmed", body: "Check your booking or contact support before trying another payment." },
    },
    sticky: { label: "Your selection" },
    results: { bar: "Sort and filter the results", sort: "Sort", filters: "Filters", active: (n) => `${counted("en", { one: "# filter", other: "# filters" })(n)} on`, modify: "Modify" },
    filters: { hint: "Results update as you choose.", show: (n) => `Show ${counted("en", { one: "# result", other: "# results" })(n)}`, showAll: "Show results" },
  },
  fr: {
    loading: "Chargement…",
    // components/ui/password-input: the eye that shows what is typed.
    passwordVisibility: { show: "Afficher le mot de passe", hide: "Masquer le mot de passe" },
    price: {
      per: { night: "nuit", day: "jour", person: "personne", roomNight: "chambre / nuit" },
      unit: (amount, per) => `${amount} / ${per}`,
      charged: (amount) => `Débité\u00A0: ${amount}`,
      original: "Prix initial",
    },
    count: {
      nights: counted("fr", { one: "# nuit", other: "# nuits" }),
      days: counted("fr", { one: "# jour", other: "# jours" }),
      travellers: counted("fr", { one: "# voyageur", other: "# voyageurs" }),
      rooms: counted("fr", { one: "# chambre", other: "# chambres" }),
      adults: counted("fr", { one: "# adulte", other: "# adultes" }),
      children: counted("fr", { one: "# enfant", other: "# enfants" }),
      results: counted("fr", { one: "# résultat", other: "# résultats" }),
    },
    localTime: (place) => (place ? `heure ${frOf(place)}` : "heure locale"),
    stepper: { label: "Étapes de la réservation", done: "terminée", stepOf: (i, n) => `Étape ${i} sur ${n}` },
    error: {
      network: { title: "La page n’a pas pu se charger", body: "Vérifiez votre connexion, puis réessayez.", action: "Réessayer" },
      generic: { title: "Un problème est survenu de notre côté", body: "Réessayez dans un instant. Si cela continue, contactez l’assistance.", action: "Réessayer" },
      notFound: { title: "Cette page est introuvable", body: "Le lien est peut-être périmé. Repartez de l’accueil.", action: "Retour à l’accueil" },
      expired: { title: "Ce tarif a expiré", body: "Les prix et les disponibilités changent. Relancez la recherche avec les mêmes critères.", action: "Relancer la recherche" },
    },
    success: {
      reference: "Référence de réservation",
      processing: { badge: "Paiement en cours", title: "Confirmation du paiement en cours", body: "Gardez cette page ouverte et ne lancez pas un second paiement." },
      awaiting_partner: { badge: "En attente", title: "Demande envoyée, en attente de confirmation", body: "Votre réservation n’est confirmée qu’une fois acceptée par le prestataire." },
      confirmed: { badge: "Confirmée", title: "Votre réservation est confirmée", body: "" },
      failed: { badge: "Non confirmé", title: "Paiement non confirmé", body: "Consultez votre réservation ou contactez l’assistance avant de payer à nouveau." },
    },
    sticky: { label: "Votre sélection" },
    results: { bar: "Trier et filtrer les résultats", sort: "Trier", filters: "Filtres", active: (n) => `${counted("fr", { one: "# filtre actif", other: "# filtres actifs" })(n)}`, modify: "Modifier" },
    filters: { hint: "Les résultats se mettent à jour avec vos choix.", show: (n) => `Afficher ${counted("fr", { one: "# résultat", other: "# résultats" })(n)}`, showAll: "Afficher les résultats" },
  },
  es: {
    loading: "Cargando…",
    // components/ui/password-input: the eye that shows what is typed.
    passwordVisibility: { show: "Mostrar la contraseña", hide: "Ocultar la contraseña" },
    price: {
      per: { night: "noche", day: "día", person: "persona", roomNight: "habitación / noche" },
      unit: (amount, per) => `${amount} / ${per}`,
      charged: (amount) => `Se cobra: ${amount}`,
      original: "Precio original",
    },
    count: {
      nights: counted("es", { one: "# noche", other: "# noches" }),
      days: counted("es", { one: "# día", other: "# días" }),
      travellers: counted("es", { one: "# viajero", other: "# viajeros" }),
      rooms: counted("es", { one: "# habitación", other: "# habitaciones" }),
      adults: counted("es", { one: "# adulto", other: "# adultos" }),
      children: counted("es", { one: "# niño", other: "# niños" }),
      results: counted("es", { one: "# resultado", other: "# resultados" }),
    },
    localTime: (place) => (place ? `hora de ${place}` : "hora local"),
    stepper: { label: "Pasos de la reserva", done: "completado", stepOf: (i, n) => `Paso ${i} de ${n}` },
    error: {
      network: { title: "La página no se ha cargado", body: "Comprueba tu conexión y vuelve a intentarlo.", action: "Reintentar" },
      generic: { title: "Algo ha fallado por nuestra parte", body: "Vuelve a intentarlo en un momento. Si continúa, contacta con asistencia.", action: "Reintentar" },
      notFound: { title: "No encontramos esta página", body: "Puede que el enlace esté caducado. Empieza de nuevo desde el inicio.", action: "Ir al inicio" },
      expired: { title: "Esta tarifa ha caducado", body: "Los precios y la disponibilidad cambian. Busca de nuevo con los mismos datos.", action: "Buscar de nuevo" },
    },
    success: {
      reference: "Referencia de la reserva",
      processing: { badge: "Pago pendiente", title: "Confirmando tu pago", body: "Mantén esta página abierta y no inicies otro pago." },
      awaiting_partner: { badge: "Pendiente", title: "Solicitud enviada, pendiente de confirmación", body: "Tu reserva solo queda confirmada cuando el proveedor la acepta." },
      confirmed: { badge: "Confirmada", title: "Tu reserva está confirmada", body: "" },
      failed: { badge: "Sin confirmar", title: "Pago no confirmado", body: "Consulta tu reserva o contacta con asistencia antes de volver a pagar." },
    },
    sticky: { label: "Tu selección" },
    results: { bar: "Ordenar y filtrar los resultados", sort: "Ordenar", filters: "Filtros", active: (n) => `${counted("es", { one: "# filtro activo", other: "# filtros activos" })(n)}`, modify: "Modificar" },
    filters: { hint: "Los resultados se actualizan con tus elecciones.", show: (n) => `Mostrar ${counted("es", { one: "# resultado", other: "# resultados" })(n)}`, showAll: "Mostrar resultados" },
  },
  pt: {
    loading: "A carregar…",
    // components/ui/password-input: the eye that shows what is typed.
    passwordVisibility: { show: "Mostrar a palavra-passe", hide: "Ocultar a palavra-passe" },
    price: {
      per: { night: "noite", day: "dia", person: "pessoa", roomNight: "quarto / noite" },
      unit: (amount, per) => `${amount} / ${per}`,
      charged: (amount) => `Débito: ${amount}`,
      original: "Preço original",
    },
    count: {
      nights: counted("pt", { one: "# noite", other: "# noites" }),
      days: counted("pt", { one: "# dia", other: "# dias" }),
      travellers: counted("pt", { one: "# viajante", other: "# viajantes" }),
      rooms: counted("pt", { one: "# quarto", other: "# quartos" }),
      adults: counted("pt", { one: "# adulto", other: "# adultos" }),
      children: counted("pt", { one: "# criança", other: "# crianças" }),
      results: counted("pt", { one: "# resultado", other: "# resultados" }),
    },
    localTime: (place) => (place ? `hora de ${place}` : "hora local"),
    stepper: { label: "Passos da reserva", done: "concluído", stepOf: (i, n) => `Passo ${i} de ${n}` },
    error: {
      network: { title: "A página não carregou", body: "Verifique a ligação e tente novamente.", action: "Tentar novamente" },
      generic: { title: "Ocorreu um problema do nosso lado", body: "Tente novamente dentro de momentos. Se continuar, contacte o apoio.", action: "Tentar novamente" },
      notFound: { title: "Não encontramos esta página", body: "A ligação pode estar desatualizada. Recomece a partir da página inicial.", action: "Ir para a página inicial" },
      expired: { title: "Esta tarifa expirou", body: "Os preços e a disponibilidade mudam. Pesquise novamente com os mesmos dados.", action: "Pesquisar novamente" },
    },
    success: {
      reference: "Referência da reserva",
      processing: { badge: "Pagamento pendente", title: "A confirmar o seu pagamento", body: "Mantenha esta página aberta e não inicie outro pagamento." },
      awaiting_partner: { badge: "Pendente", title: "Pedido enviado, a aguardar confirmação", body: "A sua reserva só fica confirmada quando o prestador a aceitar." },
      confirmed: { badge: "Confirmada", title: "A sua reserva está confirmada", body: "" },
      failed: { badge: "Não confirmado", title: "Pagamento não confirmado", body: "Consulte a reserva ou contacte o apoio antes de pagar novamente." },
    },
    sticky: { label: "A sua seleção" },
    results: { bar: "Ordenar e filtrar os resultados", sort: "Ordenar", filters: "Filtros", active: (n) => `${counted("pt", { one: "# filtro ativo", other: "# filtros ativos" })(n)}`, modify: "Alterar" },
    filters: { hint: "Os resultados atualizam-se com as suas escolhas.", show: (n) => `Mostrar ${counted("pt", { one: "# resultado", other: "# resultados" })(n)}`, showAll: "Mostrar resultados" },
  },
  ar: {
    loading: "جارٍ التحميل…",
    // components/ui/password-input: the eye that shows what is typed.
    passwordVisibility: { show: "إظهار كلمة المرور", hide: "إخفاء كلمة المرور" },
    price: {
      per: { night: "ليلة", day: "يوم", person: "شخص", roomNight: "غرفة / ليلة" },
      unit: (amount, per) => `${amount} / ${per}`,
      charged: (amount) => `المبلغ المخصوم: ${amount}`,
      original: "السعر الأصلي",
    },
    count: {
      nights: counted("ar", { zero: "# ليلة", one: "ليلة واحدة", two: "ليلتان", few: "# ليالٍ", many: "# ليلة", other: "# ليلة" }),
      days: counted("ar", { zero: "# يوم", one: "يوم واحد", two: "يومان", few: "# أيام", many: "# يومًا", other: "# يوم" }),
      travellers: counted("ar", { zero: "# مسافر", one: "مسافر واحد", two: "مسافران", few: "# مسافرين", many: "# مسافرًا", other: "# مسافر" }),
      rooms: counted("ar", { zero: "# غرفة", one: "غرفة واحدة", two: "غرفتان", few: "# غرف", many: "# غرفة", other: "# غرفة" }),
      adults: counted("ar", { zero: "# بالغ", one: "بالغ واحد", two: "بالغان", few: "# بالغين", many: "# بالغًا", other: "# بالغ" }),
      children: counted("ar", { zero: "# طفل", one: "طفل واحد", two: "طفلان", few: "# أطفال", many: "# طفلًا", other: "# طفل" }),
      results: counted("ar", { zero: "# نتيجة", one: "نتيجة واحدة", two: "نتيجتان", few: "# نتائج", many: "# نتيجة", other: "# نتيجة" }),
    },
    localTime: (place) => (place ? `بتوقيت ${place}` : "بالتوقيت المحلي"),
    stepper: { label: "خطوات الحجز", done: "مكتملة", stepOf: (i, n) => `الخطوة ${i} من ${n}` },
    error: {
      network: { title: "تعذّر تحميل الصفحة", body: "تحقق من اتصالك، ثم أعد المحاولة.", action: "إعادة المحاولة" },
      generic: { title: "حدثت مشكلة من جهتنا", body: "أعد المحاولة بعد قليل. إذا استمرت المشكلة، تواصل مع الدعم.", action: "إعادة المحاولة" },
      notFound: { title: "لم نعثر على هذه الصفحة", body: "ربما أصبح الرابط قديمًا. ابدأ من جديد من الصفحة الرئيسية.", action: "العودة إلى الصفحة الرئيسية" },
      expired: { title: "انتهت صلاحية هذا السعر", body: "الأسعار والتوفر يتغيران. ابحث من جديد بالتفاصيل نفسها.", action: "البحث من جديد" },
    },
    success: {
      reference: "مرجع الحجز",
      processing: { badge: "الدفع قيد المعالجة", title: "جارٍ تأكيد الدفع", body: "أبقِ هذه الصفحة مفتوحة ولا تبدأ عملية دفع أخرى." },
      awaiting_partner: { badge: "بانتظار التأكيد", title: "تم إرسال الطلب، بانتظار التأكيد", body: "لا يتم تأكيد حجزك إلا بعد قبول مقدّم الخدمة له." },
      confirmed: { badge: "مؤكد", title: "تم تأكيد حجزك", body: "" },
      failed: { badge: "غير مؤكد", title: "لم يتم تأكيد الدفع", body: "تحقق من حجزك أو تواصل مع الدعم قبل الدفع مجددًا." },
    },
    sticky: { label: "اختيارك" },
    results: { bar: "ترتيب النتائج وتصفيتها", sort: "ترتيب", filters: "عوامل التصفية", active: (n) => `${counted("ar", { zero: "# عامل تصفية نشط", one: "عامل تصفية نشط واحد", two: "عاملا تصفية نشطان", few: "# عوامل تصفية نشطة", many: "# عامل تصفية نشطًا", other: "# عامل تصفية نشط" })(n)}`, modify: "تعديل" },
    filters: { hint: "تتحدّث النتائج مع اختياراتك.", show: (n) => `عرض ${counted("ar", { zero: "# نتيجة", one: "نتيجة واحدة", two: "نتيجتين", few: "# نتائج", many: "# نتيجة", other: "# نتيجة" })(n)}`, showAll: "عرض النتائج" },
  },
  zh: {
    loading: "加载中…",
    // components/ui/password-input: the eye that shows what is typed.
    passwordVisibility: { show: "显示密码", hide: "隐藏密码" },
    price: {
      per: { night: "晚", day: "天", person: "人", roomNight: "间 / 晚" },
      unit: (amount, per) => `${amount} / ${per}`,
      charged: (amount) => `扣款金额：${amount}`,
      original: "原价",
    },
    count: {
      nights: counted("zh", { other: "# 晚" }),
      days: counted("zh", { other: "# 天" }),
      travellers: counted("zh", { other: "# 位旅客" }),
      rooms: counted("zh", { other: "# 间客房" }),
      adults: counted("zh", { other: "# 位成人" }),
      children: counted("zh", { other: "# 位儿童" }),
      results: counted("zh", { other: "# 个结果" }),
    },
    localTime: (place) => (place ? `${place}当地时间` : "当地时间"),
    stepper: { label: "预订步骤", done: "已完成", stepOf: (i, n) => `第 ${i} 步，共 ${n} 步` },
    error: {
      network: { title: "页面未能加载", body: "请检查网络连接，然后重试。", action: "重试" },
      generic: { title: "我们这边出现了问题", body: "请稍后重试。如果问题持续，请联系客服。", action: "重试" },
      notFound: { title: "找不到此页面", body: "链接可能已过期。请从首页重新开始。", action: "返回首页" },
      expired: { title: "此报价已过期", body: "价格和空房情况会变化。请使用相同条件重新搜索。", action: "重新搜索" },
    },
    success: {
      reference: "预订编号",
      processing: { badge: "付款处理中", title: "正在确认您的付款", body: "请保持此页面打开，不要再次付款。" },
      awaiting_partner: { badge: "待确认", title: "请求已发送，等待确认", body: "只有在服务商接受后，您的预订才会确认。" },
      confirmed: { badge: "已确认", title: "您的预订已确认", body: "" },
      failed: { badge: "未确认", title: "付款未确认", body: "再次付款前，请先查看您的预订或联系客服。" },
    },
    sticky: { label: "您的选择" },
    results: { bar: "排序和筛选结果", sort: "排序", filters: "筛选", active: (n) => `${counted("zh", { other: "已启用 # 个筛选条件" })(n)}`, modify: "修改" },
    filters: { hint: "结果会随您的选择即时更新。", show: (n) => `显示 ${counted("zh", { other: "# 个结果" })(n)}`, showAll: "显示结果" },
  },
};
